import { useMemo } from 'react';
import {
  useGetSessionQuery,
  useGetEnrichedStudentsQuery,
  useListSessionObservationsQuery,
  useSessionMedicationsDueQuery,
  useListSessionsForLocationQuery,
} from '../../store/api/localApi';
import {
  AttendanceEventShape,
  AttendanceEventType,
  SessionStatus,
} from '../../types/attendance-v2';
import { MedicationDueSlot } from '../../types';
import { minutesLate as computeMinutesLate } from './lateness';
import { offScheduleNote } from './off-schedule';
import { dublinDateString } from '../../utils/date';

/**
 * Mirrors backend `isWithinFiveMinutes` (attendance-session-service.ts): a,
 * b are "HH:MM" 24-hour strings, compared as an absolute-minute difference
 * within a ±5 min window.
 */
function isWithinFiveMinutes(a: string, b: string): boolean {
  const toMinutes = (hhmm: string) => {
    const [h, m] = hhmm.split(':').map(Number);
    return h * 60 + m;
  };
  return Math.abs(toMinutes(a) - toMinutes(b)) <= 5;
}

export interface ChildVM {
  studentId: string;
  studentName: string;
  studentDateOfBirth?: string;
  status: 'expected' | 'here' | 'gone' | 'rolled';
  lastEventAt?: string;
  /** Minutes late (clamped to 0), measured from the session's scheduled
   *  start — see lateness.ts. A child arriving before the scheduled start
   *  reads 0, never negative. */
  minutesLate: number;
  observationCount: number;
  /** DUE/OVERDUE medication slots only — see MedicationDueSlot['state']. */
  dueDoses: MedicationDueSlot[];
  /** Set for a `here` child whose roster carries them into an adjacent next
   *  class today (mirrors backend findAdjacentSessionsAfter — same
   *  location+date, next scheduledStart within 5 min of this session's
   *  scheduledEnd, not ENDED/CANCELLED). Undefined for every other status:
   *  a child who isn't present isn't rolling forward. */
  stayingOn?: { nextClassName: string };
}

export interface LiveSessionVM {
  children: ChildVM[];
  groups: { expected: ChildVM[]; here: ChildVM[]; announcedAbsent: ChildVM[] };
  counts: { here: number; expected: number; announcedAbsent: number };
  /** Set in Task 9b. */
  offSchedule?: { copy: string };
}

/**
 * Live-session view model: per-child status/counts/groups derived from the
 * session's attendance events, roster, observations and meds-due board.
 *
 * Extracted from RunSession.tsx's LiveSessionView — preserves that
 * derivation's semantics exactly (latest non-voided event per student;
 * announced-absent per Bug 11b) so this is a pure refactor, not a behaviour
 * change.
 */
export function useLiveSession(sessionId: string): LiveSessionVM {
  const { data: sessionData } = useGetSessionQuery(sessionId, { skip: !sessionId });
  const session = sessionData?.session;
  const events: AttendanceEventShape[] = sessionData?.events ?? [];
  const absentStudentIds: string[] = sessionData?.absentStudentIds ?? [];

  const { data: studentsData } = useGetEnrichedStudentsQuery(
    { locationId: session?.locationId },
    { skip: !session?.locationId }
  );
  const { data: obsData } = useListSessionObservationsQuery({ sessionId }, { skip: !sessionId });
  const { data: medsData } = useSessionMedicationsDueQuery(sessionId, { skip: !sessionId });

  // Other sessions at this location today — used to find the adjacent next
  // class for the "staying on" roll-forward marker (mirrors backend
  // findAdjacentSessionsAfter). Skipped until we know the location/date.
  const { data: locationSessionsData } = useListSessionsForLocationQuery(
    { locationId: session?.locationId ?? '', date: session?.date ?? '' },
    { skip: !session?.locationId || !session?.date }
  );

  return useMemo<LiveSessionVM>(() => {
    if (!session) {
      return {
        children: [],
        groups: { expected: [], here: [], announcedAbsent: [] },
        counts: { here: 0, expected: 0, announcedAbsent: 0 },
      };
    }

    const studentsById = new Map<
      string,
      { firstName?: string; lastName?: string; dateOfBirth?: string }
    >();
    for (const s of studentsData?.students ?? []) {
      studentsById.set(s.id, {
        firstName: s.firstName,
        lastName: s.lastName,
        dateOfBirth: s.dateOfBirth,
      });
    }

    // Per-student observation count (ACTIVE only).
    const obsCountByStudent = new Map<string, number>();
    for (const o of obsData?.observations ?? []) {
      if (o.status !== 'ACTIVE') continue;
      obsCountByStudent.set(o.studentId, (obsCountByStudent.get(o.studentId) ?? 0) + 1);
    }

    // Per-student DUE/OVERDUE medication slots.
    const dueDosesByStudent = new Map<string, MedicationDueSlot[]>();
    for (const slot of medsData?.slots ?? []) {
      if (slot.state !== 'DUE' && slot.state !== 'OVERDUE') continue;
      const list = dueDosesByStudent.get(slot.childId) ?? [];
      list.push(slot);
      dueDosesByStudent.set(slot.childId, list);
    }

    // "Late" is only a meaningful live signal when the session is being run on
    // its own day. Viewing a past-dated session (or a stale tab) would otherwise
    // show a not-yet-arrived child as thousands of minutes late (now − a
    // scheduled start days ago). Off its day, an expected child is simply
    // "expected", not "N late".
    const isToday = session.date === dublinDateString();

    // Build per-student latest-event lookup, skipping voided events.
    const latestByStudent = new Map<string, AttendanceEventShape>();
    for (const e of events) {
      if (e.voidedAt) continue;
      const prev = latestByStudent.get(e.studentId);
      if (!prev || e.at > prev.at) latestByStudent.set(e.studentId, e);
    }

    // Adjacent next class(es): same location + date, not ENDED/CANCELLED,
    // scheduledStart within 5 min of THIS session's scheduledEnd (mirrors
    // backend findAdjacentSessionsAfter). A child rolls forward into the
    // first adjacent session whose roster expects them.
    const adjacentSessions = (locationSessionsData?.sessions ?? []).filter((other) => {
      if (other.id === session.id) return false;
      if (other.status === SessionStatus.ENDED || other.status === SessionStatus.CANCELLED) {
        return false;
      }
      return isWithinFiveMinutes(other.scheduledStart, session.scheduledEnd);
    });
    const stayingOnByStudent = new Map<string, { nextClassName: string }>();
    for (const adj of adjacentSessions) {
      for (const sid of adj.expectedStudentIds) {
        if (!stayingOnByStudent.has(sid)) {
          stayingOnByStudent.set(sid, { nextClassName: adj.className });
        }
      }
    }

    const children: ChildVM[] = session.expectedStudentIds.map((sid) => {
      const studentInfo = studentsById.get(sid);
      const studentName =
        [studentInfo?.firstName, studentInfo?.lastName].filter(Boolean).join(' ').trim() ||
        latestByStudent.get(sid)?.studentName ||
        'Unnamed';
      const studentDateOfBirth = studentInfo?.dateOfBirth;
      const observationCount = obsCountByStudent.get(sid) ?? 0;
      const dueDoses = dueDosesByStudent.get(sid) ?? [];
      const latest = latestByStudent.get(sid);

      if (!latest) {
        return {
          studentId: sid,
          studentName,
          studentDateOfBirth,
          status: 'expected',
          minutesLate: isToday ? computeMinutesLate(session.scheduledStart, session.date) : 0,
          observationCount,
          dueDoses,
        };
      }

      const base = {
        studentId: sid,
        studentName,
        studentDateOfBirth,
        lastEventAt: latest.at,
        minutesLate: 0,
        observationCount,
        dueDoses,
      };

      switch (latest.type) {
        case AttendanceEventType.CHECK_IN:
        case AttendanceEventType.ROLLED_IN:
          return { ...base, status: 'here', stayingOn: stayingOnByStudent.get(sid) };
        case AttendanceEventType.ROLLED_OUT:
          return { ...base, status: 'rolled' };
        case AttendanceEventType.CHECK_OUT:
          return { ...base, status: 'gone' };
        default:
          return { ...base, status: 'expected' };
      }
    });

    const childrenById = new Map(children.map((c) => [c.studentId, c]));
    const absentSet = new Set(absentStudentIds);

    // Announced absent covers TWO shapes (Bug 11b):
    //   1. Absence filed BEFORE session start — the child was already
    //      excluded from expectedStudentIds, so they have no ChildVM at all.
    //      Synthesise one from absentStudentIds so staff still sees "parent
    //      said they're out."
    //   2. Absence filed AFTER session start — the snapshot is frozen and the
    //      child is still in `expected`. Pull them out of that bucket into
    //      the announced-absent group.
    // Any absent kid who already has a check-in event (edge case) is
    // omitted — the event trumps the declaration.
    const announcedAbsent: ChildVM[] = absentStudentIds
      .filter((sid) => {
        const c = childrenById.get(sid);
        return !c || c.status === 'expected';
      })
      .map((sid) => {
        const existing = childrenById.get(sid);
        if (existing) return existing;
        const info = studentsById.get(sid);
        const studentName =
          [info?.firstName, info?.lastName].filter(Boolean).join(' ').trim() || 'Unnamed student';
        return {
          studentId: sid,
          studentName,
          studentDateOfBirth: info?.dateOfBirth,
          status: 'expected',
          minutesLate: 0,
          observationCount: obsCountByStudent.get(sid) ?? 0,
          dueDoses: dueDosesByStudent.get(sid) ?? [],
        };
      });

    const expected = children.filter((c) => c.status === 'expected' && !absentSet.has(c.studentId));
    const here = children.filter((c) => c.status === 'here');

    return {
      children,
      groups: { expected, here, announcedAbsent },
      counts: {
        here: here.length,
        expected: expected.length,
        announcedAbsent: announcedAbsent.length,
      },
      offSchedule: offScheduleNote(session),
    };
  }, [session, events, absentStudentIds, studentsData, obsData, medsData, locationSessionsData]);
}
